import React, { useMemo } from 'react';
import styled from 'styled-components';
import { useProjectElementListStore } from '../../shared/store/projectElementStore';
import { DisplayAlignment, DisplaySize, ImageStyle, PEInfoType, TextAlignment } from '../../shared/enum/EnumRepository';
import { DocumentData } from '../../shared/dto/EntityRepository';
import { getFlexGrow, ImgContentInfoWrapperRight, InfoWrapperRight, isRightAligned, isRightAlignedOnly } from './WorkInfoRight';
import MobileBanWrapper from '../../shared/component/MobileBanWrapper';
import { InputPEDetailInfo } from '../../shared/component/headless/input/InputBody';
import MoleculeInputDiv from '../../shared/component/molecule/MoleculeInputDiv';
import { DOCUMENT_DESCRIPTION_MAX_LENGTH } from '../../shared/constants/limits';

export interface DocumentInfoRightProps {
  peId: string;
  alignment: DisplayAlignment;
  displaySize: DisplaySize;
  imageStyle: ImageStyle;
  infoType: PEInfoType;
  data: DocumentData;
}

const DocumentInfoRight: React.FC<DocumentInfoRightProps> = ({ peId, alignment, displaySize, imageStyle, infoType, data }) => {
  const handleChange = useProjectElementListStore((s) => s.updateDocument);

  const defaultAlignment = alignment || DisplayAlignment.CENTER;
  const defaultDisplaySize = displaySize || DisplaySize.BIG;
  const defaultImageStyle = imageStyle || ImageStyle.LANDSCAPE;
  const rightAligned = useMemo(
    () => isRightAligned(defaultAlignment, defaultDisplaySize, defaultImageStyle, infoType),
    [defaultAlignment, defaultDisplaySize, defaultImageStyle, infoType]
  );

  const rightAlignedOnly = useMemo(
    () => isRightAlignedOnly(defaultAlignment, defaultDisplaySize, defaultImageStyle),
    [defaultAlignment, defaultDisplaySize, defaultImageStyle]
  );

  const flexGrow = useMemo(
    () => getFlexGrow(defaultAlignment, defaultDisplaySize, defaultImageStyle),
    [defaultAlignment, defaultDisplaySize, defaultImageStyle]
  );

  return (
    <MobileBanWrapper>
      <ImgContentInfoWrapperRight $flexGrow={flexGrow} $rightAligned={rightAligned} $rightAlignedOnly={rightAlignedOnly}>
        {infoType !== PEInfoType.NONE && (
          <InfoWrapperRight
            $rightAlignedOnly={rightAlignedOnly}
            $displaySize={defaultDisplaySize}
            $imageStyle={defaultImageStyle}
          >
            <DescriptionWrapper $rightAlignedOnly={rightAlignedOnly}>
              <MoleculeInputDiv
                value={data.description}
                placeholder={"Add context for this project-related image."}
                handleChange={(e) => handleChange(peId, { description: e.target.value })}
                inputStyle={InputPEDetailInfo}
                StyledDiv={Description}
                alignment={TextAlignment.LEFT}
                maxLength={DOCUMENT_DESCRIPTION_MAX_LENGTH}
              />
            </DescriptionWrapper>
          </InfoWrapperRight>
        )}
      </ImgContentInfoWrapperRight>
    </MobileBanWrapper>
  );
};

const DescriptionWrapper = styled.div<{ $rightAlignedOnly: boolean }>`
  display: ${({ $rightAlignedOnly }) => ($rightAlignedOnly ? 'none' : 'flex')};
  width: 100%;
`;

const Description = styled.div`
  width: 100%;
  min-width: 0;
  overflow-wrap: break-word;
  word-break: break-word;
  text-align: left;
  color: ${({ theme }) => theme.colors.color_Gray_04};
  ${({ theme }) => theme.typography.Body_04};
`;


export default DocumentInfoRight;
